import { movedItems } from '@/features/menu/utils/menu-draft';
import type { Translations } from '@/i18n/locales/en';
import { formatMoney } from '@/utils/money';
import {
  menuChangeTarget,
  type MenuChange,
  type MenuChangeKind,
  type MenuTree,
} from '@restaurant/api-contracts';

type SummaryCopy = Translations['menuQuickEdit']['summary'];

type GroupKey = Exclude<keyof SummaryCopy, 'items' | 'sections'>;

const CREATES: MenuChangeKind[] = [
  'categoryCreate',
  'productCreate',
  'optionCreate',
  'optionChoiceCreate',
];

const GROUPS: [GroupKey, MenuChangeKind[]][] = [
  [
    'prices',
    ['productPrice', 'optionChoicePrice', 'productChannelPrice', 'optionChoiceChannelPrice'],
  ],
  ['added', CREATES],
  ['removed', ['categoryRemoved', 'productRemoved', 'optionRemoved', 'optionChoiceRemoved']],
  ['renamed', ['productName', 'categoryName', 'optionName', 'optionChoiceName']],
  ['descriptions', ['productDescription']],
  ['moved', ['categoryOrder', 'productOrder', 'optionOrder', 'optionChoiceOrder']],
  ['stock', ['productAvailability', 'optionChoiceAvailability']],
  ['visibility', ['productActive', 'categoryActive', 'optionChoiceActive']],
  ['allergens', ['productAllergenCodes']],
  ['labels', ['productLabels']],
  ['hours', ['productSchedule', 'categorySchedule']],
  ['rules', ['optionRules']],
  ['defaults', ['optionChoiceDefault']],
];

function placesOf(tree: MenuTree): Map<string, { product: string; section: string }> {
  const places = new Map<string, { product: string; section: string }>();
  for (const category of tree.categories) {
    for (const product of category.products) {
      const place = { product: product.id, section: category.id };
      places.set(product.id, place);
      for (const option of product.options) {
        for (const choice of option.choices) places.set(choice.id, place);
      }
    }
  }
  return places;
}

function count(changes: MenuChange[], created: Set<string>): number {
  return changes.reduce((sum, change) => {
    switch (change.kind) {
      case 'categoryOrder':
      case 'productOrder':
      case 'optionOrder':
      case 'optionChoiceOrder':
        return (
          sum +
          movedItems(change.before, change.after).size +
          change.after.filter((id) => !change.before.includes(id) && !created.has(id)).length
        );
      default:
        return sum + 1;
    }
  }, 0);
}

function uniformDelta(changes: MenuChange[]): number | null {
  const deltas = changes.flatMap((change) =>
    change.kind === 'productPrice' || change.kind === 'optionChoicePrice'
      ? [change.after - change.before]
      : [],
  );
  const first = deltas[0];
  return deltas.length === changes.length && first !== undefined && deltas.every((d) => d === first)
    ? first
    : null;
}

const signedMoney = (delta: number, currency: string) =>
  `${delta > 0 ? '+' : '−'}${formatMoney(Math.abs(delta), currency)}`;

function countsByGroup(changes: MenuChange[], copy: SummaryCopy): string {
  const created = new Set(
    changes.flatMap((change) => (CREATES.includes(change.kind) ? [menuChangeTarget(change)] : [])),
  );
  return GROUPS.flatMap(([key, kinds]) => {
    const group = changes.filter((change) => kinds.includes(change.kind));
    const n = count(group, created);
    return n > 0 ? [copy[key](n)] : [];
  }).join(' · ');
}

export function summarizeMenuDraft(
  tree: MenuTree,
  changes: MenuChange[],
  copy: SummaryCopy,
  currency: string,
): string {
  if (changes.length === 0) return '';
  const delta = uniformDelta(changes);
  if (delta === null) return countsByGroup(changes, copy);
  const lookup = placesOf(tree);
  const places = changes.flatMap((change) => {
    const place = lookup.get(menuChangeTarget(change));
    return place ? [place] : [];
  });
  return [
    signedMoney(delta, currency),
    copy.items(new Set(places.map((p) => p.product)).size),
    copy.sections(new Set(places.map((p) => p.section)).size),
  ].join(' · ');
}

export function summarizeProductDraft(
  changes: MenuChange[],
  copy: SummaryCopy,
  currency: string,
): string {
  if (changes.length === 0) return '';
  const delta = uniformDelta(changes);
  return delta === null
    ? countsByGroup(changes, copy)
    : [signedMoney(delta, currency), copy.prices(changes.length)].join(' · ');
}
